import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import TabContentBase from '../TabContentBase';
import OverviewTabContent from '../OverviewTabContent';
import ResultsTab from '../ResultsTab';
import EventsTabContent from '../EventsTabContent';
import ClubsTabContent from '../ClubsTabContent';
import NewStatsTabContent from '../NewStatsTabContent';
import StudentTabEditWrapper from '../StudentTabContent/StudentTabEditWrapper';
import TabContentStudent from '../TabContentBase/TabContentStudent';
import StudentTabContent from '../StudentTabContent';
import CollegeProspectTab from '../CollegeProspectTab';

import { changeYear, changeGameType, changeEventScheduleType, getDataForTab } from '../../../actions/profile-tab-actions';
import { updateStatsParams, getPlayerProfileVolumetricStats } from '../../../actions/profile-actions';
import { PlayerProfileTabIdentifiers } from '../../../utils/constants';
import { PROFILE_TAB_HAS_CHANGED_PROFILES } from '../../../actions/actionTypes/profile-tab-action-types';
import { LeaderboardTabTypes } from '../LocalizedRankings/constants';
import Leaderboard from '../LocalizedRankings/Leaderboard';
import { useStatsig } from '../../hooks/useStatsig';
import useStartPowerCheckout from '../../hooks/useStartPowerCheckout';
import { useCPPDynamicConfig } from '../../hooks/useCPPDynamicConfig';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../Analytics/constants';

function ProfileTabManager({
    tabs,
    activeTabIdentifier,
    displayCollegeRecruitingValues,
    onShowLoginPopUp,
    ctaData,
    search,
    isOwnProfile,
    onTabClick,
    onSavePlayerStudentInfo,
    onShowEditForm,
    onShowError,
    statsView,
}) {
    const params = useParams();
    const dispatch = useDispatch();
    const auth = useSelector(({ auth }) => auth);
    const profile = useSelector(({ profile }) => profile);
    const { hasChangedProfiles } = useSelector(({ profileTabData }) => profileTabData);
    const { OVERVIEW, RESULTS, EVENTS, CLUBS, STATS, STUDENT_INFO } = PlayerProfileTabIdentifiers;
    const { checkGate, featureGates } = useStatsig();
    const { startPowerCheckout } = useStartPowerCheckout();
    const { isCollegeProspectPowerEnabled } = useCPPDynamicConfig();
    const { claimedPlayer } = auth?.user || {};
    const [showRankingsModal, setShowRankingsModal] = useState(false);
    const [selectedLeaderboardTab, setSelectedLeaderboardTab] = useState(null);
    const [rankingData, setRankingData] = useState(null);
    const { TOP_ONE_HUNDRED, CLOSE_IN_RANK } = LeaderboardTabTypes;

    const isUserLoggedIn = !!auth?.user;
    const isCurrentUserProfile = isUserLoggedIn && ((profile?.data && claimedPlayer) ? (profile.data.id === claimedPlayer.playerId) : false);
    const isProProfile = profile?.data?.isPro;
    const isProfileIdUpdated = params.playerId === profile.playerId;
    const isActivityMetricsEnabled = checkGate(featureGates.ACTIVITY_METRICS) && !!auth?.user?.isPowered;
    const showActivityNewBadge = checkGate(featureGates.ACTIVITY_METRICS_NEW_BADGE);

    useEffect(() => {
        if (activeTabIdentifier === STATS && isActivityMetricsEnabled && isUserLoggedIn && profile.playerId) {
            dispatch(getPlayerProfileVolumetricStats(profile.playerId));
        }
    }, [activeTabIdentifier, isActivityMetricsEnabled, isUserLoggedIn, profile.playerId, profile.volumetricStats.data]);

    useEffect(() => {
        if (hasChangedProfiles) {
            if (isUserLoggedIn && activeTabIdentifier === RESULTS) {
                dispatch(getDataForTab(RESULTS));
            }
            dispatch({ type: PROFILE_TAB_HAS_CHANGED_PROFILES, payload: false });
        }
    }, [hasChangedProfiles]);

    useEffect(() => {
        if (activeTabIdentifier !== OVERVIEW && showRankingsModal) {
            setShowRankingsModal(false);
        }
    }, [activeTabIdentifier, showRankingsModal]);

    const handleOnResultGameTypeChange = (value) => {
        dispatch(changeGameType(value));
    };

    const handleOnResultYearChange = (value) => {
        dispatch(changeYear(value));
    };

    const handleOnEventScheduleTypeChange = (value) => {
        dispatch(changeEventScheduleType(value));
    };

    const handleOnSeeEventsClick = (scheduleType) => {
        if (profile.eventScheduleType !== scheduleType) {
            dispatch(changeEventScheduleType(scheduleType));
        }
        onTabClick(null, EVENTS);
    };

    const handleOnStatsParamsChange = (params) => {
        dispatch(updateStatsParams(params));
        dispatch(getDataForTab(STATS));
    };

    const onShowUpgradeToPowerPopup = () => {
        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.PLAYER_METRICS,
        });
    };

    const handleOnSegmentClick = (ranking) => {
        setSelectedLeaderboardTab(TOP_ONE_HUNDRED);
        setRankingData(ranking);
        setShowRankingsModal(true);
    };

    const handleOnRankClick = (ranking) => {
        setSelectedLeaderboardTab(CLOSE_IN_RANK);
        setRankingData(ranking);
        setShowRankingsModal(true);
    };

    const handleOnBackButtonClick = () => {
        setShowRankingsModal(false);
    };

    const renderTab = (activeTabId) => {
        switch (activeTabId) {
            case OVERVIEW:
                return (
                    showRankingsModal ? (
                        <Leaderboard
                            rankingData={rankingData}
                            selectedTab={selectedLeaderboardTab}
                            onBackButtonClick={handleOnBackButtonClick}
                        />
                    ) : (
                        <TabContentBase
                            displayOnlyMainContent={false}
                            profileData={profile.data}
                            isUserLoggedIn={isUserLoggedIn}
                            displayCollegeRecruitingValues={displayCollegeRecruitingValues}
                            isOverviewTab={true}
                            onSegmentClick={handleOnSegmentClick}
                            onRankClick={handleOnRankClick}
                        >
                            <OverviewTabContent
                                profile={profile}
                                auth={auth}
                                isProfileIdUpdated={isProfileIdUpdated}
                                isCurrentUserProfile={isCurrentUserProfile}
                                isUserLoggedIn={isUserLoggedIn}
                                isProProfile={isProProfile}
                                ctaData={ctaData}
                                search={search}
                                onSeeEventsClick={handleOnSeeEventsClick}
                                onShowLoginPopUp={onShowLoginPopUp}
                                isOnProfilePage={true}
                            />
                        </TabContentBase>
                    )
                );
            case RESULTS:
                return (
                    <ResultsTab
                        auth={auth}
                        isUserLoggedIn={isUserLoggedIn}
                        isProfileIdUpdated={isProfileIdUpdated}
                        profileData={profile.data}
                        playerId={profile.playerId}
                        selectedYear={profile.year}
                        selectedGameType={profile.gameType}
                        onGameTypeChange={handleOnResultGameTypeChange}
                        onYearChange={handleOnResultYearChange}
                        isProProfile={isProProfile}
                        displayCollegeRecruitingValues={displayCollegeRecruitingValues}
                    />
                );
            case EVENTS:
                return (
                    <TabContentBase
                        displayOnlyMainContent={false}
                        isUserLoggedIn={isUserLoggedIn}
                        profileData={profile.data}
                    >
                        <EventsTabContent
                            auth={auth}
                            isUserLoggedIn={isUserLoggedIn}
                            isProfileIdUpdated={isProfileIdUpdated}
                            onScheduleTypeChange={handleOnEventScheduleTypeChange}
                            playerId={profile.playerId}
                            isCurrentUserProfile={isCurrentUserProfile}
                            search={search}
                            ctaData={ctaData}
                            onHandleTabClick={handleOnSeeEventsClick}
                            eventScheduleType={profile.eventScheduleType}
                        />
                    </TabContentBase>
                );
            case CLUBS:
                return (
                    <TabContentBase
                        displayOnlyMainContent={false}
                        isUserLoggedIn={isUserLoggedIn}
                        profileData={profile.data}
                    >
                        <ClubsTabContent
                            isUserLoggedIn={isUserLoggedIn}
                            isProfileIdUpdated={isProfileIdUpdated}
                            isCurrentUserProfile={isCurrentUserProfile}
                        />
                    </TabContentBase>
                );
            case STATS:
                return (
                    <NewStatsTabContent
                        auth={auth}
                        isLoadingProfile={profile.isLoading}
                        profileData={profile.data}
                        statsState={profile.statsState}
                        rankings={profile.data?.playerBio?.playerRankings}
                        ptsRankings={profile.ptsRankings}
                        playerId={profile.playerId}
                        updateStatsParams={handleOnStatsParamsChange}
                        onShowUpgradeToPowerPopup={onShowUpgradeToPowerPopup}
                        isLoaded={profile.isProfileLoaded}
                        isProfileIdUpdated={isProfileIdUpdated}
                        isUserLoggedIn={isUserLoggedIn}
                        isActivityMetricsEnabled={isActivityMetricsEnabled}
                        showActivityNewBadge={showActivityNewBadge}
                        initialStatsView={statsView}
                        isOwnProfile={isOwnProfile}
                    />
                );
            case STUDENT_INFO: {
                if (isCollegeProspectPowerEnabled && isOwnProfile) {
                    return (<CollegeProspectTab />);
                }
                const studentInfoTab = tabs.find((tab) => {
                    return tab.id === STUDENT_INFO;
                });
                return (
                    studentInfoTab.showEditForm
                        ? (
                            <StudentTabEditWrapper
                                auth={auth}
                                profile={profile}
                                savePlayerStudentInfo={onSavePlayerStudentInfo}
                                showEditForm={onShowEditForm}
                                showError={onShowError}
                            />
                        ) : (
                            <TabContentStudent
                                displayOnlyMainContent={false}
                                profileData={profile.data}
                                displayCollegeRecruitingValues={displayCollegeRecruitingValues}
                                showEditForm={onShowEditForm}
                                isOwnProfile={isOwnProfile}
                            >
                                <StudentTabContent
                                    profileData={profile.data}
                                    isOwnProfile={isOwnProfile}
                                    showEditForm={onShowEditForm}
                                    user={isUserLoggedIn}
                                />
                            </TabContentStudent>
                        )
                );
            }
            default:
                break;
        }
    };

    return (
        renderTab(activeTabIdentifier)
    );
}

ProfileTabManager.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        showEditForm: PropTypes.bool,
    })).isRequired,
    activeTabIdentifier: PropTypes.number.isRequired,
    displayCollegeRecruitingValues: PropTypes.bool.isRequired,
    onShowLoginPopUp: PropTypes.func.isRequired,
    ctaData: PropTypes.objectOf(PropTypes.any).isRequired,
    search: PropTypes.objectOf(PropTypes.any).isRequired,
    isOwnProfile: PropTypes.bool,
    statsView: PropTypes.string,
    onTabClick: PropTypes.func.isRequired,
    onGetPlayerStudentInfo: PropTypes.func.isRequired,
    onSavePlayerStudentInfo: PropTypes.func.isRequired,
    onShowError: PropTypes.func.isRequired,
    onShowEditForm: PropTypes.func.isRequired,
};

ProfileTabManager.defaultProps = {
    isOwnProfile: false,
    statsView: null,
};

export default ProfileTabManager;
